// @ts-nocheck
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileCheck, AlertTriangle, TrendingUp, DollarSign, Mail } from 'lucide-react';
import { StatsCard } from '../components/StatsCard';
import { StatusBadge } from '../components/StatusBadge';
import { api } from '../lib/api';
import { useYears } from '../lib/useYears';
import { formatCurrency, formatDateTime, daysSince } from '../lib/utils';

export function Dashboard() {
  const { years, year, setYear } = useYears();
  const [summary, setSummary] = useState<Record<string, unknown> | null>(null);
  const [priority, setPriority] = useState<Record<string, unknown>[]>([]);
  const [activity, setActivity] = useState<{ recentUpdates: Record<string, unknown>[]; recentEmails: Record<string, unknown>[] } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!year) return;
    setLoading(true);
    Promise.all([
      api.dashboardSummary(year),
      api.dashboardPriority(year),
      api.recentActivity(),
    ]).then(([s, p, a]) => {
      setSummary(s);
      setPriority(p.priority);
      setActivity(a as typeof activity);
    }).finally(() => setLoading(false));
  }, [year]);

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-gray-400">Loading...</div>;
  }

  const s = summary || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          className="border rounded-lg px-3 py-2 text-sm"
        >
          {years.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Total WHT Amount"
          value={formatCurrency(Number(s.totalAmount || 0))}
          subtitle={`${s.totalTransactions || 0} transactions`}
          icon={<DollarSign size={24} />}
        />
        <StatsCard
          title="Certificates Collected"
          value={`${s.certificatesReceived || 0} / ${s.certificatesTotal || 0}`}
          subtitle={`${s.collectionPercentage || 0}% complete`}
          icon={<FileCheck size={24} />}
          color="text-green-600"
        />
        <StatsCard
          title="Collection Progress"
          value={`${s.collectionPercentage || 0}%`}
          icon={<TrendingUp size={24} />}
          color="text-blue-600"
        />
        <StatsCard
          title="Amount at Risk"
          value={formatCurrency(Number(s.amountAtRisk || 0))}
          subtitle="Missing certificates"
          icon={<AlertTriangle size={24} />}
          color="text-red-600"
        />
      </div>

      {/* Progress Bar */}
      <div className="bg-white rounded-lg border p-5">
        <h3 className="text-sm font-medium text-gray-500 mb-3">Collection Progress</h3>
        <div className="w-full bg-gray-200 rounded-full h-4">
          <div
            className="bg-green-500 h-4 rounded-full transition-all"
            style={{ width: `${s.collectionPercentage || 0}%` }}
          />
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-500">
          <span>0%</span>
          <span>{s.collectionPercentage || 0}% collected</span>
          <span>100%</span>
        </div>
      </div>

      {/* Status Breakdown */}
      {s.statusBreakdown && (
        <div className="bg-white rounded-lg border p-5">
          <h3 className="font-semibold mb-3">Status Breakdown</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {Object.entries(s.statusBreakdown as Record<string, { count: number; amount: number }>).map(
              ([status, data]) => (
                <div key={status} className="text-center p-3 rounded-lg bg-gray-50">
                  <StatusBadge status={status} />
                  <p className="text-lg font-bold mt-2">{data.count}</p>
                  <p className="text-xs text-gray-500">{formatCurrency(data.amount)}</p>
                </div>
              )
            )}
          </div>
        </div>
      )}

      {/* Priority Clients */}
      <div className="bg-white rounded-lg border">
        <div className="p-5 border-b">
          <h3 className="font-semibold">Priority Clients — Missing Certificates</h3>
          <p className="text-sm text-gray-500">Sorted by missing WHT amount (highest first)</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase">Client</th>
                <th className="px-4 py-3 text-right text-xs text-gray-500 uppercase">Missing Amount</th>
                <th className="px-4 py-3 text-center text-xs text-gray-500 uppercase">Missing Certs</th>
                <th className="px-4 py-3 text-center text-xs text-gray-500 uppercase">Last Email</th>
                <th className="px-4 py-3 text-center text-xs text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {priority.map((p) => {
                const days = daysSince(p.last_email_date as string | null);
                return (
                  <tr key={p.client_id as number} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <Link to={`/wht/clients/${p.client_id}`} className="text-blue-600 hover:underline font-medium">
                        {p.client_name as string}
                      </Link>
                      {!p.contact_email && (
                        <span className="ml-2 text-xs text-red-500">No email</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-red-600 font-medium">
                      {formatCurrency(Number(p.missing_amount))}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                        {p.missing_count as number}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-xs">
                      {days !== null ? (
                        <span className={days > 14 ? 'text-red-500' : 'text-gray-500'}>
                          {days} days ago
                        </span>
                      ) : (
                        <span className="text-gray-400">Never</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Link
                        to={`/wht/clients/${p.client_id}`}
                        className="text-xs px-3 py-1 bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                );
              })}
              {priority.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                    No data yet. Import transaction data to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Activity */}
      {activity && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg border">
            <div className="p-4 border-b">
              <h3 className="font-semibold flex items-center gap-2">
                <FileCheck size={16} /> Recent Status Changes
              </h3>
            </div>
            <div className="divide-y">
              {(activity.recentUpdates || []).map((u) => (
                <div key={u.id as number} className="px-4 py-3 text-sm">
                  <span className="font-medium">{u.client_name as string}</span>
                  <span className="mx-2 text-gray-400">&rarr;</span>
                  <StatusBadge status={u.cert_status as string} />
                  <span className="text-xs text-gray-400 ml-2">
                    {formatDateTime(u.updated_at as string)}
                  </span>
                </div>
              ))}
              {(activity.recentUpdates || []).length === 0 && (
                <p className="px-4 py-6 text-sm text-gray-400 text-center">No recent changes</p>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg border">
            <div className="p-4 border-b">
              <h3 className="font-semibold flex items-center gap-2">
                <Mail size={16} /> Recent Emails
              </h3>
            </div>
            <div className="divide-y">
              {(activity.recentEmails || []).map((e) => (
                <div key={e.id as number} className="px-4 py-3 text-sm">
                  <span className="font-medium">{e.client_name as string}</span>
                  <span className="text-gray-400 mx-1">&mdash;</span>
                  <span className="text-gray-600">{e.sent_to as string}</span>
                  <span className="text-xs text-gray-400 ml-2">
                    {formatDateTime(e.created_at as string)}
                  </span>
                </div>
              ))}
              {(activity.recentEmails || []).length === 0 && (
                <p className="px-4 py-6 text-sm text-gray-400 text-center">No emails sent yet</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
